/**
 * BikeWorkoutPickerDrawer — unified file picker for the Bike module.
 *
 * Two tabs:
 *   My Files   — user's saved FIT files (DataGrid + quick-filter, delete)
 *   Community  — shared community bike workouts (DataGrid + quick-filter)
 *
 * Props
 * -----
 * open      boolean
 * onClose   () => void
 * onSelect  (source) => void
 *   source shapes:
 *     { type: 'myfile',    fileId: number, record: Object }
 *     { type: 'community', sharedId: number, record: Object }
 * title     string  (optional, default 'Browse Workouts')
 */

import { useState, useEffect } from 'react';
import GarminCalendar from './garmin/GarminCalendar';
import {
  Drawer, Box, Typography, Tabs, Tab, Button, IconButton,
  CircularProgress, Alert, useTheme, useMediaQuery, Chip,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import { getUserFitFiles, deleteUserFile, listCommunityBikeWorkouts } from '../api/client';
import { getMyClubBikeFiles } from '../api/clubApi';

function SearchToolbar() {
  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <GridToolbarQuickFilter debounceMs={200} fullWidth size="small" />
    </Box>
  );
}

const TABS = [
  { id: 'myFiles',   labelKey: 'myFilesTab',   defaultLabel: 'My Files'  },
  { id: 'garmin',    labelKey: 'garminTab',    defaultLabel: 'Garmin'    },
  { id: 'community', labelKey: 'communityTab', defaultLabel: 'Community' },
  { id: 'clubFiles', labelKey: 'clubFilesTab', defaultLabel: 'Club Files' },
];

export default function BikeWorkoutPickerDrawer({
  open,
  onClose,
  onSelect,
  title,
}) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [tabIndex, setTabIndex] = useState(0);
  const currentTabId = TABS[tabIndex].id;

  const [myFiles, setMyFiles] = useState([]);
  const [myFilesLoading, setMyFilesLoading] = useState(false);
  const [myFilesLoaded, setMyFilesLoaded] = useState(false);

  const [communityRows, setCommunityRows] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityLoaded, setCommunityLoaded] = useState(false);

  const [clubRows, setClubRows] = useState([]);
  const [clubLoading, setClubLoading] = useState(false);
  const [clubLoaded, setClubLoaded] = useState(false);

  const [selectionMap, setSelectionMap] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectionMap({});
      setTabIndex(0);
      setError(null);
      setClubLoaded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError(null);

    if (currentTabId === 'myFiles' && !myFilesLoaded && !myFilesLoading) {
      setMyFilesLoading(true);
      getUserFitFiles()
        .then(data => {
          setMyFiles(Array.isArray(data) ? data : (data.files || []));
          setMyFilesLoaded(true);
        })
        .catch(() => setError(t('failedToLoadFiles', 'Failed to load your files')))
        .finally(() => setMyFilesLoading(false));
    }

    if (currentTabId === 'community' && !communityLoaded && !communityLoading) {
      setCommunityLoading(true);
      listCommunityBikeWorkouts()
        .then(data => {
          const raw = Array.isArray(data) ? data : (data.workouts || []);
          setCommunityRows(raw.map(r => ({ ...r, id: r.id ?? r.shared_workout_id })));
          setCommunityLoaded(true);
        })
        .catch(() => setError(t('failedToLoadCommunity', 'Failed to load community workouts')))
        .finally(() => setCommunityLoading(false));
    }

    if (currentTabId === 'clubFiles' && !clubLoaded && !clubLoading) {
      setClubLoading(true);
      getMyClubBikeFiles()
        .then(data => {
          setClubRows(Array.isArray(data) ? data.map(r => ({ ...r, id: r.id })) : []);
          setClubLoaded(true);
        })
        .catch(() => setError(t('failedToLoadClubFiles', 'Failed to load club files')))
        .finally(() => setClubLoading(false));
    }
  }, [open, currentTabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteMyFile = async (fileId, e) => {
    e.stopPropagation();
    try {
      await deleteUserFile('fit', fileId);
      setMyFiles(prev => prev.filter(f => f.id !== fileId));
      setSelectionMap(prev =>
        prev.myFiles === fileId ? { ...prev, myFiles: null } : prev
      );
    } catch {
      setError(t('failedToDelete', 'Failed to delete file'));
    }
  };

  const selectedId = selectionMap[currentTabId] ?? null;
  const canConfirm = selectedId !== null;

  const buildSource = (tabId, rowId) => {
    if (tabId === 'myFiles') {
      const record = myFiles.find(f => f.id === rowId);
      return { type: 'myfile', fileId: rowId, record };
    }
    if (tabId === 'community') {
      const record = communityRows.find(r => r.id === rowId);
      return { type: 'community', sharedId: rowId, record };
    }
    if (tabId === 'clubFiles') {
      const record = clubRows.find(r => r.id === rowId);
      return { type: 'clubFile', clubFileId: rowId, clubId: record?.club_id, record };
    }
    if (tabId === 'garmin') {
      // The calendar holds its own data; the id is all the caller needs to
      // import the activity.
      return { type: 'garmin', activityId: rowId };
    }
    return null;
  };

  const handleConfirm = () => {
    const source = buildSource(currentTabId, selectedId);
    if (source) onSelect(source);
  };

  const handleRowDoubleClick = (params, tabId) => {
    const source = buildSource(tabId, params.row.id);
    if (source) onSelect(source);
  };

  // ── Columns ──────────────────────────────────────────────────────────────
  const myFilesColumns = [
    {
      field: 'filename',
      headerName: t('fileColumn', 'File'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[100]} noWrap title={params.row.filename} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.filename}
        </Typography>
      ),
    },
    {
      field: 'workout_date', headerName: t('date', 'Date'), width: 90,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      field: 'total_distance', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${(v / 1000).toFixed(1)} km` : '-',
    },
    {
      field: 'bike_type', headerName: t('bikeType', 'Bike'), width: 80,
      renderCell: (params) => params.value
        ? <Chip label={params.value.toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
        : null,
    },
    {
      field: 'location_name', headerName: '📍', width: 120,
      renderCell: (params) => params.value
        ? (
          <Typography variant="caption" color={colors.blueAccent[400]} noWrap title={params.value}>
            {params.value}
          </Typography>
        )
        : null,
    },
    {
      field: 'description',
      headerName: t('description', 'Description'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
    {
      field: '_delete', headerName: '', width: 40, sortable: false, filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleDeleteMyFile(params.row.id, e)}
          sx={{ color: colors.redAccent[400] }}
          title={t('deleteFile', 'Delete file')}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const conditionChipColor = (cond) => {
    const map = { hot: '#ef4444', cold: '#60a5fa', windy: '#f59e0b', rainy: '#818cf8' };
    return map[cond] || colors.grey[600];
  };

  const communityColumns = [
    {
      field: 'display_name', headerName: t('sharedBy', 'Shared by'), width: 110,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value || ''}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'race_name', headerName: t('race', 'Race'), flex: 1, minWidth: 120,
      renderCell: (params) => (
        <Box>
          <Typography variant="caption" color={colors.grey[100]} noWrap title={params.value || ''}>
            {params.value || '—'}
          </Typography>
          {params.row.race_year && (
            <Typography variant="caption" color={colors.grey[500]} display="block">
              {params.row.race_year}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'race_type_display', headerName: t('raceType', 'Type'), width: 80,
      renderCell: (params) => params.value
        ? <Chip label={params.value} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
        : null,
    },
    {
      field: 'conditions', headerName: t('conditions', 'Cond.'), width: 75,
      renderCell: (params) => params.value
        ? (
          <Chip
            label={params.value}
            size="small"
            sx={{ fontSize: '0.65rem', height: 18, bgcolor: conditionChipColor(params.value), color: '#fff' }}
          />
        )
        : null,
    },
    {
      field: 'finish_time_seconds', headerName: t('time', 'Time'), width: 80,
      valueFormatter: (v) => {
        if (!v) return '-';
        const h = Math.floor(v / 3600);
        const m = Math.floor((v % 3600) / 60);
        return `${h}:${String(m).padStart(2, '0')}`;
      },
    },
    {
      field: 'avg_power_watts', headerName: t('avgPower', 'Avg W'), width: 70,
      valueFormatter: (v) => v != null ? `${Math.round(v)}W` : '-',
    },
    {
      field: 'total_distance_km', headerName: t('dist', 'Dist'), width: 70,
      valueFormatter: (v) => v != null ? `${Number(v).toFixed(1)} km` : '-',
    },
  ];

  const clubFilesColumns = [
    {
      field: 'club_name', headerName: t('club', 'Club'), width: 110,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value || ''}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'contributor_email', headerName: t('member', 'Member'), flex: 1, minWidth: 100,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value || ''}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'filename', headerName: t('fileColumn', 'File'), flex: 1, minWidth: 120,
      renderCell: (params) => (
        <Typography variant="caption" color={colors.grey[100]} noWrap title={params.value || ''}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'workout_date', headerName: t('date', 'Date'), width: 90,
      valueFormatter: (v) => v ? new Date(v).toLocaleDateString() : '-',
    },
    {
      field: 'total_distance', headerName: t('dist', 'Dist'), width: 75,
      valueFormatter: (v) => v != null ? `${(v / 1000).toFixed(1)} km` : '-',
    },
    {
      field: 'bike_type', headerName: t('bikeType', 'Bike'), width: 70,
      renderCell: (params) => params.value
        ? <Chip label={params.value.toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
        : null,
    },
    {
      field: 'description',
      headerName: t('description', 'Description'),
      flex: 1,
      minWidth: 100,
      renderCell: (params) => params.value
        ? <Typography variant="caption" color={colors.grey[300]} noWrap title={params.value}>{params.value}</Typography>
        : <Typography variant="caption" color={colors.grey[600]}>—</Typography>,
    },
  ];

  // ── DataGrid shared style ─────────────────────────────────────────────────
  const gridSx = {
    height: '100%',
    border: 'none',
    fontSize: '0.8rem',
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .MuiDataGrid-row.picker-row-selected': {
      backgroundColor: `${colors.blueAccent[900]} !important`,
    },
    '& .MuiDataGrid-row.picker-row-selected:hover': {
      backgroundColor: `${colors.blueAccent[800]} !important`,
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  };

  const renderGrid = (rows, columns, tabId, loading) => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (!rows.length) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Typography color={colors.grey[400]}>{t('noItemsFound', 'No items found')}</Typography>
        </Box>
      );
    }
    const sel = selectionMap[tabId];
    return (
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        getRowClassName={(params) => params.row.id === sel ? 'picker-row-selected' : ''}
        onRowClick={(params) =>
          setSelectionMap(prev => ({ ...prev, [tabId]: params.row.id }))
        }
        onRowDoubleClick={(params) => handleRowDoubleClick(params, tabId)}
        slots={{ toolbar: SearchToolbar }}
        disableColumnMenu
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        pageSizeOptions={[25, 50, 100]}
        sx={gridSx}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '80%',
          backgroundColor: colors.grey[800],
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        px={2} py={1.5}
        sx={{ flexShrink: 0, borderBottom: `1px solid ${colors.grey[700]}` }}
      >
        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
          {title || t('browseWorkouts', 'Browse Workouts')}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(_, newIndex) => { setTabIndex(newIndex); setError(null); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${colors.grey[700]}`,
          '& .MuiTab-root': { color: colors.grey[300] },
          '& .Mui-selected': { color: colors.blueAccent[400] },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
        }}
      >
        {TABS.map(tab => (
          <Tab key={tab.id} label={t(tab.labelKey, tab.defaultLabel)} />
        ))}
      </Tabs>

      {/* Content */}
      <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
        {error && (
          <Alert severity="error" sx={{ mx: 1, mt: 1, flexShrink: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {currentTabId === 'myFiles' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(myFiles, myFilesColumns, 'myFiles', myFilesLoading)}
          </Box>
        )}

        {currentTabId === 'garmin' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            <GarminCalendar
              sport="bike"
              selectedId={selectionMap.garmin}
              onSelect={(id) => setSelectionMap(prev => ({ ...prev, garmin: id }))}
              onConfirm={(id) => {
                setSelectionMap(prev => ({ ...prev, garmin: id }));
                onSelect({ type: 'garmin', activityId: id });
              }}
            />
          </Box>
        )}

        {currentTabId === 'community' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(communityRows, communityColumns, 'community', communityLoading)}
          </Box>
        )}

        {currentTabId === 'clubFiles' && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(clubRows, clubFilesColumns, 'clubFiles', clubLoading)}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${colors.grey[700]}`, p: 2 }}>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            sx={{
              color: colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': { borderColor: colors.grey[300] },
            }}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!canConfirm}
            fullWidth
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.blueAccent[500] },
              '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
            }}
          >
            {t('select', 'Select')}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
